import type {
  DealerOptionItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import {
  displayOldPrice,
  EqOptionType,
  formatPriceAndCurrency,
  Options,
  STORED_ARCHIVED_ITEMS
} from '@common/utils'
import {
  errorToast,
  type IconColor,
  type IconVariant,
  MolsonAccordion,
  MolsonArchived,
  MolsonAutocomplete,
  MolsonBox,
  MolsonCheckbox,
  MolsonCustomChip,
  MolsonGrid,
  MolsonSpinner,
  MolsonTextField
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ArchivedNotification } from '../archived-notification/ArchivedNotification'
import type { HandleOptionChangeFunctionProps } from '../types'
import { useDealerFitsUtils } from './dealerFitsUtils'

type DealerFitOptionsProps = {
  name: string
  options: Record<string, DealerOptionItemDto[]>
  ungroupedOptions: DealerOptionItemDto[]
}

export interface DealerOptionItemDtoWithChecked extends DealerOptionItemDto {
  checked: boolean
}

export type CommonDealerFitProps = {
  expandedFamilies?: Set<string>
  handleOptionChange: HandleOptionChangeFunctionProps
  handleUpdateDealerFitQuantity?: ({
    actionType,
    quotationItemId,
    optionId,
    itemQuantity
  }: {
    actionType: 'decrease' | 'increase'
    quotationItemId: string
    optionId: number
    itemQuantity: number
  }) => void
  quotationItem: MachineQuotationItemDto
  quotationItemOldPrices?: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  selectedDealerOptions: DealerOptionItemDtoWithChecked[]
  setExpandedFamilies?: (value: string) => void
  setSelectedDealerOptions?: (value: DealerOptionItemDtoWithChecked[]) => void
  updateDealerFitQuantity?: (props: {
    actionType: 'decrease' | 'increase'
    itemQuantity: number
    optionId: number
  }) => void
  closeExpandedFamilyOrGroup?: (
    eqOptionType: EqOptionType,
    familyOrGroup: string
  ) => void
}

const defaultLoading = {
  id: -1,
  load: false
}

const SignWritingFamily = 'Signwriting'

export const DealerFitOptions = ({
  expandedFamilies,
  handleOptionChange,
  name = '',
  options,
  quotationItem,
  quotationItemOldPrices,
  selectedDealerOptions,
  setExpandedFamilies,
  updateDealerFitQuantity,
  ungroupedOptions,
  closeExpandedFamilyOrGroup
}: DealerFitOptionsProps & CommonDealerFitProps) => {
  const theme = molsonUseTheme()
  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const [selectedOption, setSelectedOption] = useState<null | string>(null)
  const [expanded, setExpanded] = useState(expandedFamilies?.has(name))

  const [selectedTag, setSelectedTag] = useState<string>('')

  const [loading, setLoading] = useState(defaultLoading)

  const { attachmentId, equipmentId, quoteId, specId } = useParams()

  const isWithinMachine = Boolean(equipmentId && attachmentId)

  const quotationId = quoteId ?? specId

  const {
    getOptionChecked,
    getItemQuantity,
    getDealerFitOptionlabel,
    getDisplayedOptions,
    hasMultipleBrands,
    displayDropdown,
    archivedFamilies,
    revisedBrand,
    revisedFamily,
    itemKey,
    selectedFamilyOptions
  } = useDealerFitsUtils({
    dealerFitName: name,
    selectedOption,
    selectedTag,
    options,
    ungroupedOptions,
    selectedDealerOptions,
    quotationItem,
    quotationItemOldPrices
  })

  // Track previous dealerOptions to clear loading when mutation completes
  const prevDealerOptionsRef = useRef(quotationItem.dealerOptions)
  useEffect(() => {
    if (prevDealerOptionsRef.current !== quotationItem.dealerOptions) {
      prevDealerOptionsRef.current = quotationItem.dealerOptions
      setLoading(defaultLoading)
    }
  }, [quotationItem.dealerOptions])

  const handleChange = (newValue: null | string) => {
    setSelectedOption(newValue)
    setSelectedTag('')
  }

  const handleDealerFitOption = (
    event: ChangeEvent<HTMLInputElement>,
    dealerOption: DealerOptionItemDto
  ) => {
    if (loading.load && dealerOption.id === loading.id) {
      return
    }

    const addAttachmentOption = isWithinMachine
      ? Options.ADD_DEALER_FIT_WITHIN_MACHINE
      : Options.ADD_DEALER_FIT

    const removeAttachmentOption = isWithinMachine
      ? Options.REMOVE_DEALER_FIT_WITHIN_MACHINE
      : Options.REMOVE_DEALER_FIT

    const eqOption = event.target.checked
      ? addAttachmentOption
      : removeAttachmentOption

    setLoading({
      id: dealerOption.id as number,
      load: true
    })

    handleOptionChange({
      machineId: isWithinMachine ? equipmentId : undefined,
      option: eqOption,
      optionId: Number(dealerOption.id),
      quotationId: quotationId as string
    })
    // Loading state is cleared by the useEffect when quotationItem.dealerOptions changes
  }

  const onIncreaseDecrease = (
    action: 'decrease' | 'increase',
    option: DealerOptionItemDto
  ) => {
    if (loading.load && option.id === loading.id) {
      return
    }

    try {
      setLoading({
        id: option.id as number,
        load: true
      })

      updateDealerFitQuantity?.({
        actionType: action,
        itemQuantity: Number(getItemQuantity(option)),
        optionId: option.id as number
      })
    } catch (error) {
      console.error(error)
      const errorMessage = t('failedToIncreaseDecrease', {
        action
      })
      errorToast(errorMessage)
    } finally {
      setLoading(defaultLoading)
    }
  }

  // Filter out brands that only have archived options
  const availableBrands = Object.entries(options).filter(([_, brandOptions]) =>
    brandOptions.some(option => !option.isArchived)
  )

  const nonEmptySubtypes = displayDropdown
    ? ungroupedOptions.filter(
        option =>
          option.type !== '' &&
          (hasMultipleBrands ? option.brand === selectedOption : true) &&
          !option.isArchived
      )
    : ungroupedOptions.filter(
        option => option.type !== '' && !option.isArchived
      )

  const uniqueSubtypes = Array.from(
    new Set(nonEmptySubtypes.map(option => option.subType2).filter(Boolean))
  )

  const handleChangeTag = (tagName: string) => {
    if (selectedTag === tagName) {
      setSelectedTag('')
    } else {
      setSelectedTag(tagName)
    }
  }

  useEffect(() => {
    if (
      revisedBrand &&
      displayDropdown &&
      expandedFamilies?.has(itemKey) &&
      name === revisedFamily
    ) {
      setSelectedOption(revisedBrand)
    }
  }, [
    revisedBrand,
    displayDropdown,
    expandedFamilies,
    itemKey,
    revisedFamily,
    name
  ])

  useEffect(() => {
    setExpanded(expandedFamilies?.has(name))
  }, [expandedFamilies, name])

  let hasFamilyPriceDifference = false
  if (
    selectedFamilyOptions?.length &&
    quotationItemOldPrices?.dealerOptions?.length
  ) {
    for (const option of selectedFamilyOptions) {
      const oldOption = quotationItemOldPrices.dealerOptions.find(
        oldItem => oldItem.id === option.id
      )

      if (displayOldPrice(option.dnp ?? 0, oldOption?.dnp ?? 0)) {
        hasFamilyPriceDifference = true
        break
      }
    }
  }

  const getSummaryIcon = () => {
    if (archivedFamilies?.includes(name)) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (hasFamilyPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const summaryIcon = getSummaryIcon()

  const selectedCount = selectedFamilyOptions?.length ?? 0

  const countBadge =
    selectedCount > 0 ? (
      <MolsonBox
        component="span"
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: 100,
          color: theme.palette.secondary[50],
          fontSize: 16,
          fontWeight: 600,
          width: 30,
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {selectedCount}
      </MolsonBox>
    ) : undefined

  return (
    <MolsonAccordion
      onChange={(_, toggle) => {
        setExpandedFamilies?.(itemKey)

        if (!toggle) {
          closeExpandedFamilyOrGroup?.(EqOptionType.DEALER_FIT, name)
        }

        setExpanded(toggle)
      }}
      summaryIcon={summaryIcon?.icon as IconVariant}
      summaryIconColor={summaryIcon?.color as IconColor}
      additionalSummaryComponent={countBadge}
      withTooltip={
        name.toLowerCase() === SignWritingFamily.toLowerCase()
          ? {
              description: t('signWritingDescription'),
              title: name
            }
          : undefined
      }
      expanded={expandedFamilies?.has(itemKey) || expanded}
      primaryText={name}
    >
      {displayDropdown && (
        <MolsonAutocomplete
          getOptionLabel={(option: string) => {
            return option
          }}
          onChange={(_, newValue: null | string | string[]) => {
            if (newValue && typeof newValue === 'string') {
              handleChange(newValue)
            } else {
              setSelectedOption('')
              sessionStorage.removeItem(STORED_ARCHIVED_ITEMS.BRAND)
              sessionStorage.removeItem(STORED_ARCHIVED_ITEMS.FAMILY)
              setSelectedTag('')
            }
          }}
          renderInput={params => {
            return (
              <MolsonTextField
                {...params}
                placeholder={t('selectBrand') ?? ''}
              />
            )
          }}
          options={availableBrands.map(([brand]) => brand)}
          value={selectedOption}
        />
      )}

      {uniqueSubtypes.length > 0 ? (
        <MolsonBox
          display="flex"
          flexWrap="wrap"
          gap={1}
          mt={displayDropdown ? 2.75 : 0}
        >
          {uniqueSubtypes.map(subtype => (
            <MolsonCustomChip
              key={subtype}
              onClick={() => handleChangeTag(subtype ?? '')}
              selected={selectedTag === subtype}
            >
              {subtype}
            </MolsonCustomChip>
          ))}
        </MolsonBox>
      ) : null}
      <MolsonGrid
        container={true}
        mt={displayDropdown || uniqueSubtypes.length > 0 ? 2.75 : 0}
        spacing={1}
      >
        <MolsonBox
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          paddingX={1}
          width="100%"
        >
          {(getDisplayedOptions() ?? []).map(option => {
            const dataSearchString =
              `${option.type ?? ''} ${option.subType1 ?? ''} ${option.subType2 ?? ''}`
                .replace(/"/g, '')
                .trim()

            if (option.isArchived) {
              const selectedItem = selectedDealerOptions.find(
                item => item.id === option.id
              )
              return (
                <MolsonBox
                  display="flex"
                  flexDirection="column"
                  key={option.id}
                >
                  <MolsonArchived
                    label={`${option.type} ${option.subType1 ?? ''} ${
                      option.subType2 ?? ''
                    }`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleDealerFitOption(event, option)
                    }
                    secondLabel={
                      selectedDealerOptions?.some(item => item.id === option.id)
                        ? `(${formatPriceAndCurrency(option.dnp as number)})`
                        : undefined
                    }
                    checked={selectedItem?.checked}
                    id={itemKey}
                  />
                  <ArchivedNotification text={t('unavailableItem')} />
                </MolsonBox>
              )
            } else {
              const isItemLoading = loading.load && loading.id === option.id
              return (
                <MolsonBox
                  key={option.id}
                  data-search={dataSearchString}
                  data-eq-type={EqOptionType.DEALER_FIT}
                  data-family={option.family || option.group}
                  sx={{ position: 'relative' }}
                >
                  {isItemLoading && (
                    <MolsonBox
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1
                      }}
                    >
                      <MolsonSpinner size={20} />
                    </MolsonBox>
                  )}
                  <MolsonCheckbox
                    label={`${option.type} ${option.subType1 ?? ''} ${
                      option.subType2 ?? ''
                    }`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleDealerFitOption(event, option)
                    }
                    checked={!!getOptionChecked(option)}
                    disabled={isItemLoading}
                    handleDecrease={() =>
                      onIncreaseDecrease('decrease', option)
                    }
                    handleIncrease={() =>
                      onIncreaseDecrease('increase', option)
                    }
                    key={option.id}
                    quantityValue={getItemQuantity(option)}
                    secondLabel={getDealerFitOptionlabel(option)}
                    withQuantityUpdate={getOptionChecked(option)}
                  />
                </MolsonBox>
              )
            }
          })}
        </MolsonBox>
      </MolsonGrid>
    </MolsonAccordion>
  )
}
